/**
 * Branded HTML for company notification emails (invoice, payment receipt, etc.).
 * Table-based layout for Gmail / Outlook compatibility.
 */

import { formatMessageDisplayCalendarDate } from '@/services/consultantOnboardingNotification.helpers.js';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoneyEmail(n) {
  if (n === null || n === undefined || n === '') return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayValue(value) {
  const v = value != null && String(value).trim() !== '' ? String(value).trim() : '—';
  return escapeHtml(v);
}

function metaRowsHtml(rows) {
  const items = (rows || []).filter((r) => r && r.label);
  if (!items.length) return '';
  let cells = '';
  for (let i = 0; i < items.length; i += 2) {
    const left = items[i];
    const right = items[i + 1];
    cells += `
      <tr>
        <td width="50%" valign="top" style="padding:0 8px 14px 0;">
          <div style="font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;margin-bottom:4px;">${escapeHtml(left.label)}</div>
          <div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.4;word-break:break-word;">${displayValue(left.value)}</div>
        </td>
        <td width="50%" valign="top" style="padding:0 0 14px 8px;">
          ${right ? `
          <div style="font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;margin-bottom:4px;">${escapeHtml(right.label)}</div>
          <div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.4;word-break:break-word;">${displayValue(right.value)}</div>` : '&nbsp;'}
        </td>
      </tr>`;
  }
  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;">
      ${cells}
    </table>`;
}

function amountSectionHtml(title, rows) {
  const items = (rows || []).filter((r) => r && r.label);
  if (!items.length) return '';
  const rowHtml = items
    .map((r) => {
      const isTotal = r.emphasize;
      return `
      <tr>
        <td style="padding:10px 0;border-bottom:1px solid #f1f5f9;font-size:13px;color:#475569;">${escapeHtml(r.label)}</td>
        <td align="right" style="padding:10px 0;border-bottom:1px solid #f1f5f9;font-size:${isTotal ? '16px' : '13px'};font-weight:${isTotal ? '800' : '600'};color:#0f172a;">${displayValue(r.value)}</td>
      </tr>`;
    })
    .join('');
  return `
    <div style="margin-top:20px;padding-top:16px;border-top:1px solid #e2e8f0;">
      <div style="font-size:11px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#64748b;margin:0 0 10px;">${escapeHtml(title)}</div>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;">
        ${rowHtml}
      </table>
    </div>`;
}

/**
 * @param {{
 *   headline: string,
 *   docType?: string,
 *   heroLabel: string,
 *   heroAmount: string,
 *   metaRows: { label: string, value?: string }[],
 *   amountSections?: { title: string, rows: { label: string, value?: string, emphasize?: boolean }[] }[],
 *   footerNote?: string,
 * }} opts
 */
export function buildModernCompanyNotificationEmailHtml(opts) {
  const headline = escapeHtml(opts.headline || 'Notification');
  const docType = escapeHtml(opts.docType || 'Anna Books');
  const heroLabel = escapeHtml(opts.heroLabel || 'Amount');
  const heroAmount = escapeHtml(opts.heroAmount || '—');
  const footerNote = escapeHtml(opts.footerNote || 'This is an automated notification from Anna Books.');

  const amountBlocks = (opts.amountSections || [])
    .map((sec) => amountSectionHtml(sec.title, sec.rows))
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<meta name="x-apple-disable-message-reformatting"/>
<title>${headline}</title>
</head>
<body style="margin:0;padding:0;width:100%;background-color:#eef2f7;-webkit-text-size-adjust:100%;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#eef2f7;">
<tr><td align="center" style="padding:24px 12px 32px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:560px;width:100%;">
<tr><td style="border-radius:16px 16px 0 0;overflow:hidden;background:#0f4c81;background:linear-gradient(145deg,#0f4c81 0%,#1e6bb8 55%,#2b8fd9 100%);padding:24px 24px 20px;">
  <div style="font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:11px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.85);margin-bottom:6px;">${docType}</div>
  <div style="font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:22px;font-weight:700;color:#ffffff;line-height:1.25;margin-bottom:18px;">${headline}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.2);border-radius:12px;">
  <tr><td style="padding:14px 16px;font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
    <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.9);margin-bottom:4px;">${heroLabel}</div>
    <div style="font-size:28px;font-weight:800;color:#ffffff;line-height:1.1;">${heroAmount}</div>
  </td></tr>
  </table>
</td></tr>
<tr><td style="background:#ffffff;border-radius:0 0 16px 16px;padding:22px 24px 24px;box-shadow:0 12px 32px rgba(15,23,42,0.08);font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
  ${metaRowsHtml(opts.metaRows)}
  ${amountBlocks}
  <div style="margin-top:22px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center;font-size:11px;color:#94a3b8;line-height:1.5;">${footerNote}</div>
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

export function buildInvoiceNotificationEmailHtml(payload) {
  const invNo = String(payload.invoiceNo || '').trim() || '—';
  const invoiceDate = formatMessageDisplayCalendarDate(payload.invoiceDate) || payload.invoiceDate;
  const dueDate = formatMessageDisplayCalendarDate(payload.dueDate) || payload.dueDate;
  const metaRows = [
    { label: 'Company', value: payload.companyName },
    { label: 'Customer', value: payload.customerName },
    { label: 'Invoice date', value: invoiceDate },
    { label: 'Due date', value: dueDate },
    { label: 'Customer email', value: payload.customerEmail },
    { label: 'Phone', value: payload.phoneNumber },
    { label: 'Contact', value: payload.contactName },
  ];
  if (payload.orderNumber != null && String(payload.orderNumber).trim()) {
    metaRows.splice(3, 0, { label: 'Order #', value: payload.orderNumber });
  }

  const amountRows = [
    { label: 'Subtotal', value: `$${formatMoneyEmail(payload.subtotal)}` },
  ];
  const bonus = Number(payload.bonusAmount) || 0;
  if (bonus > 0) amountRows.push({ label: 'Bonus', value: `$${formatMoneyEmail(bonus)}` });
  const discount = Number(payload.discountAmount) || 0;
  if (discount > 0) amountRows.push({ label: 'Discount', value: `−$${formatMoneyEmail(discount)}` });
  amountRows.push(
    { label: 'Tax', value: `$${formatMoneyEmail(payload.taxAmount)}` },
    { label: 'Total', value: `$${formatMoneyEmail(payload.totalAmount)}`, emphasize: true },
    { label: 'Status', value: payload.status || 'Sent' }
  );

  return buildModernCompanyNotificationEmailHtml({
    headline: `Invoice ${invNo}`,
    docType: payload.companyName || 'Anna Books',
    heroLabel: 'Invoice total',
    heroAmount: `$${formatMoneyEmail(payload.totalAmount)}`,
    metaRows,
    amountSections: [{ title: 'Amounts', rows: amountRows }],
    footerNote: `Invoice ${invNo} · PDF attached when available · Anna Books notification`,
  });
}

export function buildReceivePaymentNotificationEmailHtml(payload) {
  const ref = String(payload.reference || '').trim() || '—';
  const paymentDate = formatMessageDisplayCalendarDate(payload.paymentDate) || payload.paymentDate;
  const metaRows = [
    { label: 'Company', value: payload.companyName },
    { label: 'Customer', value: payload.customerName },
    { label: 'Reference', value: ref },
    { label: 'Payment date', value: paymentDate },
    { label: 'Payment method', value: payload.paymentMethod },
    { label: 'Deposit to', value: payload.depositAccountName },
  ];
  if (payload.chequeNo != null && String(payload.chequeNo).trim()) {
    metaRows.push({ label: 'Cheque / ref no', value: payload.chequeNo });
  }

  const amountRows = [
    { label: 'Amount received', value: `$${formatMoneyEmail(payload.amountReceived)}`, emphasize: true },
    { label: 'Payment applied', value: `$${formatMoneyEmail(payload.totalPayment)}` },
    { label: 'Write-off', value: `$${formatMoneyEmail(payload.totalWriteOff)}` },
    { label: 'Credits applied', value: `$${formatMoneyEmail(payload.totalAppliedCredits)}` },
    { label: 'Customer balance', value: `$${formatMoneyEmail(payload.clientBalance)}` },
  ];
  if (Number(payload.totalDiscount) > 0) {
    amountRows.splice(3, 0, { label: 'Discount', value: `$${formatMoneyEmail(payload.totalDiscount)}` });
  }

  return buildModernCompanyNotificationEmailHtml({
    headline: `Payment ${ref}`,
    docType: payload.companyName || 'Anna Books',
    heroLabel: 'Amount received',
    heroAmount: `$${formatMoneyEmail(payload.amountReceived)}`,
    metaRows,
    amountSections: [{ title: 'Payment summary', rows: amountRows }],
    footerNote: `Payment ${ref} · Receipt PDF attached when available · Anna Books notification`,
  });
}

/**
 * @param {{
 *   consultantName?: string,
 *   fromDate?: string,
 *   toDate?: string,
 *   hours?: string|number,
 *   status?: string,
 *   createdAt?: string,
 *   companyName?: string,
 *   customerName?: string,
 *   vendorName?: string,
 * }} payload
 */
export function buildTimesheetCreatedNotificationEmailHtml(payload) {
  const consultant = String(payload.consultantName || '').trim() || 'Consultant';
  const fromLabel = formatMessageDisplayCalendarDate(payload.fromDate) || payload.fromDate || '—';
  const toLabel = formatMessageDisplayCalendarDate(payload.toDate) || payload.toDate || '—';
  const period = `${fromLabel} – ${toLabel}`;
  const hoursRaw = payload.hours != null && payload.hours !== '' ? String(payload.hours) : '';
  const hoursDisplay = hoursRaw ? `${hoursRaw} hrs` : '—';

  const metaRows = [];
  if (payload.companyName != null && String(payload.companyName).trim()) {
    metaRows.push({ label: 'Company', value: payload.companyName });
  }
  if (payload.customerName != null && String(payload.customerName).trim()) {
    metaRows.push({ label: 'Customer', value: payload.customerName });
  }
  if (payload.vendorName != null && String(payload.vendorName).trim()) {
    metaRows.push({ label: 'Vendor', value: payload.vendorName });
  }
  metaRows.push(
    { label: 'Consultant', value: consultant },
    { label: 'Period', value: period },
    { label: 'Status', value: payload.status || 'Time Sheet Created' },
    { label: 'Recorded at', value: payload.createdAt || '—' }
  );

  return buildModernCompanyNotificationEmailHtml({
    headline: consultant,
    docType: payload.companyName || 'Anna Books',
    heroLabel: 'Hours logged',
    heroAmount: hoursDisplay,
    metaRows,
    footerNote: 'New timesheet · Anna Books notification',
  });
}
